import type { Address, PublicClient, WalletClient } from "viem";
import type { DeploymentReference } from "./public-v2";

const ALLOWED_RPC_METHODS = new Set([
  "eth_chainId",
  "eth_call",
  "eth_getBlockByNumber",
  "eth_getCode",
  "eth_getLogs",
  "eth_getTransactionByHash",
  "eth_getTransactionReceipt",
]);
const LOOKUP_BODY_LIMIT = 524_288;
const LOOKUP_DEADLINE_MS = 8_000;
const ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/;
const HASH_PATTERN = /^0x[0-9a-fA-F]{64}$/;

function canonicalWalletChainId(value: unknown): unknown {
  if (typeof value !== "number") return value;
  if (!Number.isSafeInteger(value) || value <= 0) throw new TypeError("wallet chain id is invalid");
  return `0x${value.toString(16)}`;
}

type RequestArgs = { method: string; params?: readonly unknown[] };
type RequestOptions = { retryCount?: number; dedupe?: boolean; signal?: AbortSignal };
type RawRequest = (args: RequestArgs, options?: RequestOptions) => Promise<unknown>;

export type V2PaidLaunchBrowserPorts = {
  wallet: { request(args: RequestArgs): Promise<unknown> };
  rpc: { request(args: RequestArgs, signal: AbortSignal): Promise<unknown> };
  lookup: { get(input: { reservationId: `0x${string}`; signal: AbortSignal }): Promise<{ status: number; body: unknown }> };
  dispose(): void;
};

export function createV2PaidLaunchBrowserPorts(input: {
  deployment: DeploymentReference;
  apiOrigin: string;
  selectedAccount: Address;
  walletClient: WalletClient;
  publicClient: PublicClient;
  isCurrent(): boolean;
  fetcher?: typeof fetch;
}): V2PaidLaunchBrowserPorts {
  const { deployment, selectedAccount, walletClient, publicClient, isCurrent } = input;
  const account = typeof selectedAccount === "string" ? selectedAccount.toLowerCase() : "";
  if (!ADDRESS_PATTERN.test(account) || account === `0x${"0".repeat(40)}`) throw new TypeError("invalid selected wallet");
  if (deployment.chainId !== 8453 || !ADDRESS_PATTERN.test(deployment.registryAddress)) throw new TypeError("invalid deployment identity");
  if (walletClient.chain?.id !== deployment.chainId || walletClient.account?.address?.toLowerCase() !== account) {
    throw new TypeError("selected wallet does not match deployment");
  }
  if (publicClient.chain?.id !== deployment.chainId) throw new TypeError("public client does not match deployment");

  const rawOrigin = input.apiOrigin;
  let apiOrigin: string;
  try {
    const parsed = new URL(rawOrigin);
    const loopback = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1" || parsed.hostname === "[::1]";
    if (parsed.username || parsed.password || parsed.search || parsed.hash || (parsed.pathname !== "/" && parsed.pathname !== "") ||
        (parsed.protocol !== "https:" && !(parsed.protocol === "http:" && loopback)) ||
        rawOrigin.replace(/\/$/, "") !== parsed.origin) throw new TypeError();
    apiOrigin = parsed.origin;
  } catch {
    throw new TypeError("invalid reservation API origin");
  }

  const walletRequest = walletClient.request as unknown as RawRequest;
  const rpcRequest = publicClient.request as unknown as RawRequest;
  const fetcher = input.fetcher ?? fetch;
  let disposed = false;

  const assertDispatchAllowed = () => {
    if (disposed || !isCurrent()) throw new Error("paid-launch wallet or client selection changed");
  };
  const abortError = () => new DOMException("The operation was aborted", "AbortError");
  const raceSignal = <T>(promise: Promise<T>, signal: AbortSignal): Promise<T> => new Promise((resolve, reject) => {
    let settled = false;
    const onAbort = () => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", onAbort);
      reject(abortError());
    };
    if (!signal.aborted) signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener("abort", onAbort);
        reject(error);
      },
    );
    if (signal.aborted) onAbort();
  });

  const wallet = {
    async request(args: RequestArgs): Promise<unknown> {
      assertDispatchAllowed();
      if (!args || typeof args !== "object" || !["eth_chainId", "eth_accounts", "eth_sendTransaction"].includes(args.method)) {
        throw new Error("wallet method is not allowed");
      }
      if (args.method === "eth_chainId" || args.method === "eth_accounts") {
        if (args.params !== undefined && args.params.length !== 0) throw new Error("wallet request parameters are invalid");
        const result = await walletRequest({ method: args.method }, { retryCount: 0, dedupe: false });
        return args.method === "eth_chainId" ? canonicalWalletChainId(result) : result;
      }

      const params = args.params;
      if (!params || params.length !== 1) throw new Error("wallet transaction is invalid");
      const transaction = params[0];
      if (!transaction || typeof transaction !== "object" || Array.isArray(transaction)) throw new Error("wallet transaction is invalid");
      const tx = transaction as Record<string, unknown>;
      if (Object.keys(tx).sort().join(",") !== "data,from,to,value" ||
          typeof tx.from !== "string" || tx.from.toLowerCase() !== account ||
          typeof tx.to !== "string" || !ADDRESS_PATTERN.test(tx.to) || tx.to === `0x${"0".repeat(40)}` ||
          typeof tx.data !== "string" || !/^0x(?:[0-9a-fA-F]{2})*$/.test(tx.data) ||
          (typeof tx.value !== "string" && typeof tx.value !== "bigint") ||
          (() => { try { return BigInt(tx.value as string | bigint) !== 0n; } catch { return true; } })()) {
        throw new Error("wallet transaction is invalid");
      }
      // Once a wallet request has entered the signer, preserve its original result even if the UI is disposed.
      return walletRequest({ method: "eth_sendTransaction", params: [transaction] }, { retryCount: 0, dedupe: false });
    },
  };

  const rpc = {
    async request(args: RequestArgs, signal: AbortSignal): Promise<unknown> {
      assertDispatchAllowed();
      if (!args || !ALLOWED_RPC_METHODS.has(args.method)) throw new Error("RPC method is not allowed");
      if (!(signal instanceof AbortSignal)) throw new TypeError("RPC cancellation signal is required");
      if (signal.aborted) throw abortError();
      if (args.method === "eth_getLogs") {
        if (!Array.isArray(args.params) || args.params.length !== 1) throw new Error("RPC log filter is invalid");
        const filter = args.params[0];
        if (!filter || typeof filter !== "object" || Array.isArray(filter)) throw new Error("RPC log filter is invalid");
        const value = filter as Record<string, unknown>;
        const quantity = (item: unknown) => typeof item === "string" && /^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(item) ? BigInt(item) : null;
        const from = quantity(value.fromBlock); const to = quantity(value.toBlock);
        if (Object.keys(value).some((key) => !["address", "fromBlock", "toBlock", "topics"].includes(key)) ||
            typeof value.address !== "string" || !ADDRESS_PATTERN.test(value.address) ||
            from === null || to === null || to < from || to - from > 100_000n ||
            !Array.isArray(value.topics) || value.topics.length < 1 || value.topics.length > 4 ||
            value.topics.some((topic) => typeof topic !== "string" && !(Array.isArray(topic) && topic.length <= 16 && topic.every((item) => typeof item === "string")))) {
          throw new Error("RPC log filter is invalid");
        }
      }
      return rpcRequest(args, { signal, retryCount: 0, dedupe: false });
    },
  };

  const lookup = {
    async get({ reservationId, signal }: { reservationId: `0x${string}`; signal: AbortSignal }): Promise<{ status: number; body: unknown }> {
      assertDispatchAllowed();
      if (!HASH_PATTERN.test(reservationId) || reservationId === `0x${"0".repeat(64)}`) throw new TypeError("invalid reservation identifier");
      if (!(signal instanceof AbortSignal)) throw new TypeError("lookup cancellation signal is required");
      if (signal.aborted) throw abortError();

      const controller = new AbortController();
      const abortFromCaller = () => controller.abort();
      signal.addEventListener("abort", abortFromCaller, { once: true });
      const timer = setTimeout(() => controller.abort(), LOOKUP_DEADLINE_MS);
      let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
      let bodyReachedEof = false;
      try {
        const url = `${apiOrigin}/v2/purchases/by-reservation/${reservationId}`;
        const response = await raceSignal(fetcher(url, {
          method: "GET", credentials: "omit", cache: "no-store", redirect: "error", signal: controller.signal,
        }), controller.signal);
        if (!response.body) throw new Error("reservation lookup response is unavailable");
        reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8", { fatal: true });
        let byteLength = 0;
        let text = "";
        for (;;) {
          const part = await raceSignal(reader.read(), controller.signal);
          if (part.done) { bodyReachedEof = true; break; }
          byteLength += part.value.byteLength;
          if (byteLength > LOOKUP_BODY_LIMIT) throw new Error("reservation lookup response is too large");
          text += decoder.decode(part.value, { stream: true });
        }
        text += decoder.decode();
        let body: unknown;
        try { body = JSON.parse(text) as unknown; }
        catch { throw new Error("reservation lookup response is invalid"); }
        return { status: response.status, body };
      } catch {
        if (controller.signal.aborted) throw abortError();
        throw new Error("reservation lookup is unavailable");
      } finally {
        clearTimeout(timer);
        signal.removeEventListener("abort", abortFromCaller);
        if (reader) {
          if (!bodyReachedEof) {
            controller.abort();
            const cancellation = reader.cancel().then(() => undefined, () => undefined);
            let cleanupTimer: ReturnType<typeof setTimeout> | undefined;
            await Promise.race([cancellation, new Promise<void>((resolve) => {
              cleanupTimer = setTimeout(resolve, 1_000);
            })]);
            if (cleanupTimer !== undefined) clearTimeout(cleanupTimer);
          }
          try { reader.releaseLock(); } catch { /* An outstanding read will release its lock when it settles. */ }
        }
      }
    },
  };

  return {
    wallet,
    rpc,
    lookup,
    dispose() { disposed = true; },
  };
}
